import React, { Component } from 'react'
import mapboxgl from 'mapbox-gl'
import classnames from 'classnames'

import { detectPassiveEventSupport } from '../../helpers'

import nodes from '../../data/nodes.json'
import networkLines from '../../data/networkLines.json'
import segmentFrames from '../../data/segmentFrames.json'
import triangles from '../../data/triangles.json'
import pingMapLines from '../../data/pingMapLines.json'
import pingMapNodes from '../../data/pingMapNodes.json'
import pingMapPolygons from '../../data/pingMapPolygons.json'
import car from '../../assets/map-car.png'

const STYLE = 'mapbox://styles/mapbox/dark-v9'

const pink = 'rgb(244, 128, 103)'
// const darkPink = 'rgb(164, 96, 81)'
const blue = 'rgb(47, 128, 237)'
// const darkBlue = 'rgb(25, 73, 138)'
const green = '#27AE60'
// const darkGreen = '#17703d'

let viewport = {
  altitude: 1,
  longitude: -73.974423,
  latitude: 40.699154,
  zoom: 12,
  minZoom: 1,
  maxZoom: 17,
  pitch: 40.5,
  bearing: -27.396674584323023
}

class MapStory extends Component {
  constructor(props) {
    super(props)
    this.state = {
      mapLoaded: false,
      width: window.innerWidth * 0.5,
      height: window.innerHeight,
      showFallback: false
    }
    this.map = null
    this.frameCount = 0
    this.currentAnimationFrameID = null
  }

  componentWillMount = () => {
    const options = { passive: detectPassiveEventSupport() }
    window.addEventListener('resize', () => this.resizeMapToViewport(), options)
  }

  componentDidMount = () => {
    const mapboxSupport = mapboxgl.supported()
    if (window.innerWidth > 760 && mapboxSupport) {
      mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN
      this.map = new mapboxgl.Map({
        container: 'mapbox-map', // container id
        style: STYLE,
        center: [-73.98, 40.741],
        zoom: 12,
        bearing: 0,
        pitch: 0,
        interactive: false,
        transition: {
          duration: 1500,
          delay: 0
        }
      })
      this.map.on('load', this.addLayers)
    } else if (window.innerWidth > 760 && !mapboxSupport) {
      this.setState({ showFallback: true })
    }
  }

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.resizeMapToViewport)
  }

  resizeMapToViewport = () => {
    const width = window.innerWidth * 0.5
    const height = window.innerHeight
    this.setState({ width, height })
  }

  mergeViewport = newViewport =>
    Object.assign({}, { ...viewport }, { ...newViewport })

  addMapSteps = steps =>
    steps.map(step => <MapTile key={step.key} step={step} />)

  addLayers = () => {
    this.map.addSource('nodeData', {
      data: nodes,
      type: 'geojson'
    })

    this.map.addSource('networkLines', {
      data: networkLines,
      type: 'geojson'
    })

    this.map.addSource('polygons', {
      data: triangles,
      type: 'geojson'
    })

    this.map.addSource('routeLines', {
      data: segmentFrames,
      type: 'geojson'
    })

    this.map.addSource('carOne', {
      type: 'geojson',
      data: this.movePoint(0, 0)
    })

    this.map.addSource('pingLinesPerFrame', {
      type: 'geojson',
      data: this.incrementFromArray(pingMapLines, this.frameCount)
    })

    this.map.addSource('pingNodesPerFrame', {
      type: 'geojson',
      data: this.incrementFromArray(pingMapNodes, this.frameCount)
    })

    this.map.addSource('pingPolygonsPerFrame', {
      type: 'geojson',
      data: this.incrementFromArray(pingMapPolygons, this.frameCount)
    })

    this.map.addLayer({
      id: 'polygons',
      type: 'fill',
      source: 'polygons',
      paint: {
        'fill-color': blue,
        'fill-opacity': 0
      }
    })

    this.map.addLayer({
      id: 'ping-polygons',
      type: 'fill',
      source: 'pingPolygonsPerFrame',
      paint: {
        'fill-color': blue,
        'fill-opacity': 0
      }
    })

    this.map.addLayer({
      id: 'nodes-heatmap',
      type: 'circle',
      source: 'nodeData',
      layout: {
        visibility: 'visible'
      },
      paint: {
        'circle-blur': 5,
        'circle-color': [
          'match',
          ['get', 'color'],
          'blue',
          blue,
          'pink',
          'rgba(0,0,0,0)',
          'green',
          'rgba(0,0,0,0)',
          blue
        ],
        'circle-radius': 60,
        'circle-opacity': 1,
        'circle-radius-transition': { duration: 1000, delay: 0 }
      }
    })

    this.map.addLayer({
      id: 'routeLines',
      type: 'line',
      source: 'routeLines',
      layout: {
        visibility: 'visible',
        'line-cap': 'round'
      },
      paint: {
        'line-color': pink,
        'line-width': 8,
        'line-opacity': 0
      }
    })

    this.map.addLayer({
      id: 'network',
      type: 'line',
      source: 'networkLines',
      layout: {
        visibility: 'visible',
        'line-cap': 'round'
      },
      paint: {
        'line-color': blue,
        'line-width': 1,
        'line-opacity': 0,
        'line-dasharray': [1, 2],
        'line-opacity-transition': { duration: 1000, delay: 500 },
        'line-dasharray-transition': { duration: 1000, delay: 2000 }
      }
    })

    this.map.addLayer({
      id: 'ping-lines',
      type: 'line',
      source: 'pingLinesPerFrame',
      layout: {
        visibility: 'visible',
        'line-cap': 'round'
      },
      paint: {
        'line-color': green,
        'line-width': 4,
        'line-opacity': 0,
        'line-dasharray': [1, 2]
      }
    })

    this.map.addLayer({
      id: 'ping-nodes',
      type: 'circle',
      source: 'pingNodesPerFrame',
      layout: {
        visibility: 'visible'
      },
      paint: {
        'circle-blur': 1,
        'circle-color': [
          'match',
          ['get', 'color'],
          'blue',
          blue,
          'pink',
          pink,
          'green',
          green,
          blue
        ],
        'circle-radius': 45,
        'circle-opacity': 0,
        'circle-radius-transition': { duration: 400, delay: 0 }
      }
    })

    this.map.addLayer({
      id: 'nodes',
      type: 'circle',
      source: 'nodeData',
      layout: {
        visibility: 'visible'
      },
      paint: {
        'circle-pitch-alignment': 'map',
        'circle-color': [
          'match',
          ['get', 'color'],
          'blue',
          blue,
          'pink',
          pink,
          'green',
          green,
          blue
        ],
        'circle-radius': 0,
        'circle-opacity': 1
      }
    })

    this.map.loadImage(car, (err, img) => {
      this.map.addImage('sprite', img)
      this.map.addLayer({
        id: 'car',
        type: 'symbol',
        source: 'carOne',
        layout: {
          'icon-image': 'sprite',
          'icon-rotation-alignment': 'map',
          'icon-size': 0.8
        }
      })
      this.setState({ mapLoaded: true })
    })
  }

  componentWillReceiveProps = nextProps => {
    if (nextProps.currentMapIndex !== this.props.currentMapIndex) {
      const { content } = this.props
      const currentStep = content.steps[nextProps.currentMapIndex]
      viewport = this.mergeViewport(currentStep.viewport)
      if (this.map) {
        this.map.easeTo({
          center: [viewport.longitude, viewport.latitude],
          pitch: viewport.pitch,
          bearing: viewport.bearing,
          zoom: viewport.zoom,
          duration: 2000,
          easing: t => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t)
        })
      }
    }
  }

  animateMarker = () => {
    if (
      this.frameCount < segmentFrames.coordinates.length &&
      this.props.currentSectionKey === 'mapStory'
    ) {
      const segments = segmentFrames.coordinates
      const currentCarCoord = segments[this.frameCount]
      this.map.getSource('carOne').setData(this.movePoint(currentCarCoord))
      this.map.getSource('routeLines').setData(this.dropLines())
      this.map
        .getSource('pingLinesPerFrame')
        .setData(this.incrementFromArray(pingMapLines, this.frameCount))
      this.map
        .getSource('pingNodesPerFrame')
        .setData(this.incrementFromArray(pingMapNodes, this.frameCount))
      this.map
        .getSource('pingPolygonsPerFrame')
        .setData(this.incrementFromArray(pingMapPolygons, this.frameCount))
      this.currentAnimationFrameID = requestAnimationFrame(this.animateMarker)
      this.frameCount++
    } else {
      window.cancelAnimationFrame(this.currentAnimationFrameID)
    }
  }

  movePoint = coord => {
    return {
      type: 'Point',
      coordinates: coord
    }
  }

  dropLines = () => {
    return {
      type: 'LineString',
      coordinates: [...segmentFrames.coordinates].slice(this.frameCount)
    }
  }

  incrementFromArray = (arr, frame) => arr[frame]

  render() {
    const { mapLock, currentMapIndex, content } = this.props

    // choose which layers to show
    if (this.state.mapLoaded && this.map) {
      if (currentMapIndex >= 0) {
        this.map.setPaintProperty('nodes', 'circle-radius', 3)
        // this.map.setPaintProperty('nodes-blur', 'circle-radius', 3)
      } else {
        this.map.setPaintProperty('nodes', 'circle-radius', 0)
      }

      if (currentMapIndex > 0) {
        this.map.setPaintProperty('nodes-heatmap', 'circle-radius', 0)
        this.map.setPaintProperty('nodes', 'circle-radius', 5)
        this.map.setPaintProperty('network', 'line-opacity', 1)
        this.map.setPaintProperty('network', 'line-dasharray', [1])
      } else {
        this.map.setPaintProperty('nodes-heatmap', 'circle-radius', 60)
        this.map.setPaintProperty('network', 'line-opacity', 0)
        this.map.setPaintProperty('network', 'line-dasharray', [1, 2])
      }

      if (currentMapIndex > 1) {
        this.map.setPaintProperty('polygons', 'fill-opacity', 0.1)
      } else {
        this.map.setPaintProperty('polygons', 'fill-opacity', 0)
      }

      if (currentMapIndex > 2) {
        this.map.setPaintProperty('ping-polygons', 'fill-opacity', 0.5)
        this.map.setPaintProperty('polygons', 'fill-opacity', 0.1)
        this.map.setPaintProperty('routeLines', 'line-opacity', 1)
        this.map.setPaintProperty('ping-lines', 'line-opacity', 1)
        this.map.setPaintProperty('ping-nodes', 'circle-opacity', 0.5)
        this.map.setLayoutProperty('car', 'icon-size', 0.5)
        this.map.setPaintProperty('nodes', 'circle-radius', 5)
        this.animateMarker(0)
      } else {
        this.map.setPaintProperty('ping-polygons', 'fill-opacity', 0)
        this.map.setPaintProperty('ping-lines', 'line-opacity', 0)
        this.map.setPaintProperty('ping-nodes', 'circle-opacity', 0)
        this.map.setPaintProperty('polygons', 'fill-color', 'rgb(47, 128, 237)')
        this.map.setPaintProperty('routeLines', 'line-opacity', 0)
        this.map.setLayoutProperty('car', 'icon-size', 0)
        if (this.currentAnimationFrameID) {
          window.cancelAnimationFrame(this.currentAnimationFrameID)
        }
      }
    }

    if (currentMapIndex < 3) {
      this.frameCount = 0
    }

    const showFallbackClasses = classnames({
      showFallback: this.state.showFallback
    })
    return (
      <section id={'mapStory'}>
        <img
          className={`mapbox-image-fallback ${showFallbackClasses}`}
          src="/publicAssets/mapStory/fallback-1.png"
          alt=""
        />
        <img
          className={`mapbox-image-fallback ${showFallbackClasses}`}
          src="/publicAssets/mapStory/fallback-2.png"
          alt=""
        />
        <img
          className={`mapbox-image-fallback ${showFallbackClasses}`}
          src="/publicAssets/mapStory/fallback-3.png"
          alt=""
        />
        <img
          className={`mapbox-image-fallback ${showFallbackClasses}`}
          src="/publicAssets/mapStory/fallback-4.png"
          alt=""
        />

        {window.innerWidth < 760 ? null : (
          <div id={'mapbox'} className={mapLock}>
            <div id="mapbox-map" />
          </div>
        )}

        <div id={'mapStory-steps-container'}>
          {this.addMapSteps(content.steps)}
        </div>
      </section>
    )
  }
}

function MapTile({ step }) {
  return (
    <div className={'mapTile-container'}>
      <div className={'mapTile'}>
        <div className={'mapTile-heading'}>
          {step.icon ? (
            <img
              alt={'Card Icon'}
              className={'smallIcon'}
              src={step.icon.src}
            />
          ) : null}
          <h2>{step.h}</h2>
        </div>
        <div className={'mapTile-content'}>
          <p>{step.p}</p>
          {step.img ? (
            <img
              className={'mapTile-diagram'}
              alt={'diagram'}
              src={step.img.src}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default MapStory



// WEBPACK FOOTER //
// ./src/Views/Home/MapStory.js